/** One selectable option built from a raw item, plus the item it came from. */
export interface ChoiceOption<TItem> {
  item: TItem;
  key: string;
  label: string;
}

/** The options a source of raw items yields, plus notices about what was left out. */
export interface ChoiceOptionsResult<TItem> {
  notices: readonly string[];
  options: readonly ChoiceOption<TItem>[];
}

/**
 * Builds combobox options from raw items, dropping what cannot be shown and
 * reporting why. An item without a key is unusable (nothing to select by), an
 * item without a label is unusable (nothing to show), and a second item that
 * repeats an earlier key is a duplicate from the source, not a fresh option.
 * Two kept options that end up sharing a label are disambiguated as
 * "label (key)", so a picker never shows two identical-looking rows.
 *
 * Pure data function: same inputs -> same output, no side effects.
 */
export function toChoiceOptions<TItem>(
  items: readonly TItem[],
  getKey: (item: TItem) => string | undefined,
  getLabel: (item: TItem) => string | undefined,
): ChoiceOptionsResult<TItem> {
  const notices: string[] = [];
  const kept: ChoiceOption<TItem>[] = [];
  const seenKeys = new Set<string>();
  const duplicateKeys: string[] = [];
  let missingKeyCount = 0;
  let missingLabelCount = 0;

  for (const item of items) {
    const key = getKey(item);
    if (key === undefined) {
      missingKeyCount += 1;
      continue;
    }

    const label = getLabel(item);
    if (label === undefined || label.length === 0) {
      missingLabelCount += 1;
      continue;
    }

    if (seenKeys.has(key)) {
      duplicateKeys.push(key);
      continue;
    }
    seenKeys.add(key);
    kept.push({ item, key, label });
  }

  if (missingKeyCount > 0) {
    notices.push(`${missingKeyCount} options left out: no key`);
  }
  if (missingLabelCount > 0) {
    notices.push(`${missingLabelCount} options left out: no name`);
  }
  for (const key of duplicateKeys) {
    notices.push(`Duplicate option left out: ${key}`);
  }

  const labelCounts = new Map<string, number>();
  for (const option of kept) {
    labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
  }

  const options = kept.map(option =>
    (labelCounts.get(option.label) ?? 0) > 1
      ? { ...option, label: `${option.label} (${option.key})` }
      : option,
  );

  return { notices, options };
}

/** Case-insensitive "contains" filter on `label`. An empty term matches everything. */
export function filterByLabel<TOption extends { label: string }>(
  options: readonly TOption[],
  term: string,
): readonly TOption[] {
  if (term.length === 0) return options;
  const needle = term.toLowerCase();
  return options.filter(option => option.label.toLowerCase().includes(needle));
}
